import { baseApi } from 'modules/core/services/api/base-api'

// Response shapes mirror the server's BusinessCreditUsage in
// apps/server/src/billing/services/credit.service.ts (the source of truth).
// Keep in sync — this endpoint is consumed via RTK Query, not the generated client.
export type CreditUsageCategory = 'image' | 'video' | 'text' | 'other'

export interface CreditUsageDayBucket {
  /** Calendar day, `YYYY-MM-DD`, oldest first. Empty days are present with zeros. */
  date: string
  image: number
  video: number
  text: number
  other: number
  total: number
}

export interface CreditUsageSourceSlice {
  source: string
  category: CreditUsageCategory
  credits: number
}

export interface CreditUsageMemberSlice {
  userId: string | null
  name: string | null
  credits: number
}

export interface BusinessCreditUsage {
  rangeDays: number
  totals: {
    consumed: number
    granted: number
    creatives: number
  }
  bySource: CreditUsageSourceSlice[]
  byDay: CreditUsageDayBucket[]
  byMember: CreditUsageMemberSlice[]
}

export interface CreditUsageArgs {
  businessId: string
  days?: number
}

export interface CreditTransaction {
  id: string
  type: string
  amount: number
  source: string
  creativeId?: string
  description?: string
  userId?: string
  createdAt: string
}

export interface CreditTransactionHistory {
  transactions: CreditTransaction[]
  total: number
}

export interface CreditTransactionsArgs {
  businessId: string
  limit?: number
  offset?: number
}

export const creditUsageApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    getCreditUsage: builder.query<BusinessCreditUsage, CreditUsageArgs>({
      query: ({ businessId, days = 30 }) => ({
        url: `/v1/business/${businessId}/billing/usage`,
        method: 'GET',
        params: { days },
      }),
      providesTags: ['CreditUsage'],
    }),
    getCreditTransactions: builder.query<CreditTransactionHistory, CreditTransactionsArgs>({
      query: ({ businessId, limit = 15, offset = 0 }) => ({
        url: `/v1/business/${businessId}/billing/transactions`,
        method: 'GET',
        params: { limit, offset },
      }),
      providesTags: ['CreditUsage'],
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetCreditUsageQuery,
  useGetCreditTransactionsQuery,
  useLazyGetCreditTransactionsQuery,
} = creditUsageApi
